import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { isJwtExpired } from '../utils/jwt-util';

function readOAuth2CookieToken(): string | null {
  const cookies = document.cookie.split(';');
  const tokenCookie = cookies.find((cookie) => cookie.trim().startsWith('oauth2_token='));
  if (!tokenCookie) {
    return null;
  }
  const cookieToken = decodeURIComponent(tokenCookie.split('=').slice(1).join('=').trim());
  return cookieToken && cookieToken.length > 0 ? cookieToken : null;
}

function clearOAuth2Cookie(): void {
  document.cookie = 'oauth2_token=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'oauth2_token=; Path=/; Domain=.scryber.ai; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT';
}

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state) => {
  const router = inject(Router);

  // Prefer a fresh OAuth cookie over a stale JWT left in localStorage from an earlier session
  const cookieToken = readOAuth2CookieToken();
  if (cookieToken) {
    localStorage.setItem('jwt_token', cookieToken);
    clearOAuth2Cookie();
  }

  const token = localStorage.getItem('jwt_token');
  if (token && !isJwtExpired(token)) {
    return true;
  }
  if (token && isJwtExpired(token)) {
    localStorage.removeItem('jwt_token');
  }
  
  // Fallback: Check if token is in URL query parameters (in case token-extractor didn't run)
  // Use fast regex match for speed
  const search = window.location.search;
  const tokenMatch = search.match(/[?&]token=([^&]*)/);
  
  if (tokenMatch) {
    const urlToken = decodeURIComponent(tokenMatch[1]);
    // SECURITY FIX: Only grant access if token is valid and non-empty
    // Empty token parameter (e.g., ?token=) should not bypass authentication
    if (urlToken && urlToken.length > 0) {
      localStorage.setItem('jwt_token', urlToken);
      // Clean up URL by removing token parameter
      let newSearch = search.replace(/[?&]token=[^&]*/, '');
      // If result starts with &, replace with ? (token was not first parameter)
      if (newSearch.startsWith('&')) {
        newSearch = '?' + newSearch.substring(1);
      }
      // Remove trailing & if present
      newSearch = newSearch.replace(/&$/, '');
      // If only ? remains, remove it (no parameters left)
      if (newSearch === '?') {
        newSearch = '';
      }
      const newUrl = window.location.origin + window.location.pathname + (newSearch ? newSearch : '');
      window.history.replaceState({}, document.title, newUrl);
      return true;
    }
    // Token parameter exists but is empty - clean up URL and deny access
    let newSearch = search.replace(/[?&]token=[^&]*/, '');
    if (newSearch.startsWith('&')) {
      newSearch = '?' + newSearch.substring(1);
    }
    newSearch = newSearch.replace(/&$/, '');
    if (newSearch === '?') {
      newSearch = '';
    }
    const newUrl = window.location.origin + window.location.pathname + (newSearch ? newSearch : '');
    window.history.replaceState({}, document.title, newUrl);
    // Do not return true - fall through to redirect to login
  }
  
  const lateCookieToken = readOAuth2CookieToken();
  if (lateCookieToken) {
    localStorage.setItem('jwt_token', lateCookieToken);
    clearOAuth2Cookie();
    const newUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    return true;
  }
  
  router.navigate(['/login']);
  return false;
};
